import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { Quotation, QuotationStatus } from "@/mocks/quotationsData";
import { statusBadgeColors } from "@/mocks/quotationsData";
import { computeSubtotal, computeTaxTotal, computeTotal, companyInfo } from "@/mocks/quotationsData";
import { useToast } from "@/components/base/Toast";
import { SendQuotationModal } from "./SendQuotationModal";
import { fetchApi } from "@/services/apiService";

type Props = {
  quotations: Quotation[];
  onRefresh?: () => void;
};

export function QuotationTable({ quotations, onRefresh }: Props) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [menuOpenId, setMenuOpenId] = useState<number | string | null>(null);
  const [sendModalOpen, setSendModalOpen] = useState<Quotation | null>(null);
  const [deletingId, setDeletingId] = useState<number | string | null>(null);

  const formatCurrency = (val: number) => {
    if (!val) return "₹0";
    if (val >= 100000) return "₹" + (val / 100000).toFixed(1) + "L";
    return "₹" + Number(val).toLocaleString("en-IN");
  };

  const formatDate = (d: string) => {
    if (!d) return "—";
    const date = new Date(d);
    return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const handleDownload = useCallback((q: Quotation) => {
    showToast("Generating PDF...", "info");
    const container = document.createElement("div");
    container.className = "quotation-print-area";
    container.style.cssText = "display:none;";
    const items = q.items || [];
    const total = Number(q.total_amount) || computeTotal(items, q.discount || 0);
    const subtotal = computeSubtotal(items);
    const taxTotal = computeTaxTotal(items);
    container.innerHTML = generatePrintHtml(q, subtotal, taxTotal, total);
    document.body.appendChild(container);
    setTimeout(() => {
      window.print();
      setTimeout(() => {
        document.body.removeChild(container);
      }, 500);
    }, 100);
  }, [showToast]);

  const handleSendFromMenu = (q: Quotation) => {
    setMenuOpenId(null);
    setSendModalOpen(q);
  };

  const handleStatusChange = async (q: Quotation, newStatus: QuotationStatus) => {
    setMenuOpenId(null);
    try {
      await fetchApi(`/quotations/${q.id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      showToast(`${q.quoteNo || (q as any).quote_number} marked as ${newStatus}`, newStatus === "Accepted" ? "success" : "info");
      if (onRefresh) onRefresh();
    } catch (err: any) {
      showToast(err.message || "Failed to update status", "error");
    }
  };

  const handleDuplicate = async (q: Quotation) => {
    setMenuOpenId(null);
    try {
      const qNum = `QT-${Math.floor(1000 + Math.random() * 9000)}`;
      await fetchApi("/quotations", {
        method: "POST",
        body: JSON.stringify({
          quote_number: qNum,
          lead_id: q.leadId || (q as any).lead_id,
          client_name: q.leadName || (q as any).client_name,
          client_company: q.leadCompany || (q as any).client_company,
          client_email: (q as any).leadEmail || (q as any).client_email,
          client_phone: (q as any).leadPhone || (q as any).client_phone,
          agent: q.agent,
          status: "Draft",
          valid_until: q.validUntil || (q as any).valid_until,
          items: q.items,
          discount: q.discount,
          terms: q.terms,
        }),
      });
      showToast(`Quotation duplicated as ${qNum}`, "success");
      if (onRefresh) onRefresh();
    } catch (err: any) {
      showToast(err.message || "Failed to duplicate quotation", "error");
    }
  };

  const handleDelete = async (q: Quotation) => {
    setMenuOpenId(null);
    if (!window.confirm(`Are you sure you want to delete ${q.quoteNo || (q as any).quote_number}?`)) return;
    setDeletingId(q.id);
    try {
      await fetchApi(`/quotations/${q.id}`, { method: "DELETE" });
      showToast("Quotation deleted successfully", "success");
      if (onRefresh) onRefresh();
    } catch (err: any) {
      showToast(err.message || "Failed to delete quotation", "error");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-xs uppercase text-gray-500 tracking-wider">
              <th className="text-left px-4 py-3 font-semibold">Quote No</th>
              <th className="text-left px-4 py-3 font-semibold">Lead / Client</th>
              <th className="text-left px-4 py-3 font-semibold">Agent</th>
              <th className="text-center px-4 py-3 font-semibold">Items</th>
              <th className="text-right px-4 py-3 font-semibold">Amount</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-left px-4 py-3 font-semibold">Valid Until</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {quotations.map((q) => {
              const items = q.items || [];
              const total = Number(q.total_amount) || computeTotal(items, q.discount || 0);
              const quoteNum = q.quoteNo || (q as any).quote_number || `QT-${q.id}`;
              const leadName = q.leadName || (q as any).client_name || "—";
              const leadCompany = q.leadCompany || (q as any).client_company || "";
              const agentName = q.agent || "Unassigned";
              const validUntil = q.validUntil || (q as any).valid_until;
              const status = q.status || "Draft";

              return (
                <tr
                  key={q.id}
                  className={`hover:bg-gray-50/70 transition-colors cursor-pointer ${deletingId === q.id ? "opacity-40 pointer-events-none" : ""}`}
                  onClick={() => navigate(`/quotations/${q.id}`)}
                >
                  <td className="px-4 py-3">
                    <span className="font-semibold text-indigo-600 text-xs">{quoteNum}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900">{leadName}</span>
                      {leadCompany && <span className="text-xs text-gray-500">{leadCompany}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-700 font-medium">{agentName}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-gray-700">{items.length} items</span>
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-gray-900">
                    {formatCurrency(total)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusBadgeColors[status] || "bg-gray-100 text-gray-700"}`}>
                      {status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{formatDate(validUntil)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => navigate(`/quotations/${q.id}`)}
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                        title="View"
                      >
                        <i className="ri-eye-line text-sm" />
                      </button>
                      <button
                        onClick={() => navigate(`/quotations/${q.id}/edit`)}
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                        title="Edit"
                      >
                        <i className="ri-edit-line text-sm" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDownload(q); }}
                        className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                        title="Download PDF"
                      >
                        <i className="ri-download-line text-sm" />
                      </button>
                      <div className="relative">
                        <button
                          onClick={() => setMenuOpenId(menuOpenId === q.id ? null : q.id)}
                          className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                          title="More"
                        >
                          <i className="ri-more-2-fill text-sm" />
                        </button>
                        {menuOpenId === q.id && (
                          <>
                            <div className="fixed inset-0 z-30" onClick={() => setMenuOpenId(null)} />
                            <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl border border-gray-200 shadow-lg z-40 py-1">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleSendFromMenu(q); }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap text-left cursor-pointer"
                              >
                                <i className="ri-send-plane-line text-gray-400" />
                                Send to Lead
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleStatusChange(q, "Accepted"); }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap text-left cursor-pointer"
                              >
                                <i className="ri-check-line text-green-500" />
                                Mark as Accepted
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleStatusChange(q, "Rejected"); }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap text-left cursor-pointer"
                              >
                                <i className="ri-close-line text-red-500" />
                                Mark as Rejected
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDuplicate(q); }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap text-left cursor-pointer"
                              >
                                <i className="ri-file-copy-line text-gray-400" />
                                Duplicate
                              </button>
                              <hr className="my-1 border-gray-100" />
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDelete(q); }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap text-left cursor-pointer"
                              >
                                <i className="ri-delete-bin-line" />
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Send Quotation Modal */}
      {sendModalOpen && (
        <SendQuotationModal
          open={true}
          onClose={() => { setSendModalOpen(null); if (onRefresh) onRefresh(); }}
          quoteId={sendModalOpen.id}
          quoteNo={sendModalOpen.quoteNo || (sendModalOpen as any).quote_number}
          leadName={sendModalOpen.leadName || (sendModalOpen as any).client_name}
          leadEmail={(sendModalOpen as any).leadEmail || (sendModalOpen as any).client_email || ""}
          totalFormatted={formatCurrency(Number(sendModalOpen.total_amount) || computeTotal(sendModalOpen.items || [], sendModalOpen.discount || 0))}
          validUntil={sendModalOpen.validUntil || (sendModalOpen as any).valid_until}
          agent={sendModalOpen.agent}
          companyName={companyInfo.name}
        />
      )}
    </div>
  );
}

function generatePrintHtml(
  q: Quotation,
  subtotal: number,
  taxTotal: number,
  total: number
) {
  const quoteNum = q.quoteNo || (q as any).quote_number || `QT-${q.id}`;
  const leadName = q.leadName || (q as any).client_name || "—";
  const leadCompany = q.leadCompany || (q as any).client_company || "";
  const agentName = q.agent || "Unassigned";
  const quoteDate = q.date || (q as any).quote_date || new Date().toISOString().split("T")[0];
  const validUntil = q.validUntil || (q as any).valid_until || "—";
  const items = q.items || [];

  const fmtDate = (d: string) => {
    if (!d || d === "—") return "—";
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };
  const fmtCurr = (v: number) => "₹" + Number(v || 0).toLocaleString("en-IN");
  const itemsHtml = items.map((item, idx) => {
    const amt = (Number(item.qty) || 0) * (Number(item.rate) || 0) * (1 + (Number(item.taxPercent) || 0) / 100);
    return `<tr><td style="padding:8px;border:1px solid #d1d5db;font-size:11px;">${idx + 1}</td><td style="padding:8px;border:1px solid #d1d5db;font-size:11px;font-weight:500;">${item.description}</td><td style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:center;">${item.qty}</td><td style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:right;">${fmtCurr(item.rate)}</td><td style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:center;">${item.taxPercent || 0}%</td><td style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:right;font-weight:500;">${fmtCurr(amt)}</td></tr>`;
  }).join("");

  return `<div style="font-family:Inter,system-ui,sans-serif;padding:20px 0;">
<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;">
<div><div style="width:48px;height:48px;background:#4f46e5;border-radius:8px;display:flex;align-items:center;justify-content:center;margin-bottom:8px;"><span style="color:white;font-weight:bold;font-size:14px;">PM</span></div><h2 style="font-size:18px;font-weight:bold;margin:0 0 4px;">${companyInfo.name}</h2><p style="font-size:12px;color:#6b7280;margin:0;">${companyInfo.address}</p><p style="font-size:12px;color:#6b7280;margin:0;">${companyInfo.phone} | ${companyInfo.email}</p><p style="font-size:11px;color:#9ca3af;margin:4px 0 0;">GSTIN: ${companyInfo.gstin}</p></div>
<div style="text-align:right;"><h1 style="font-size:24px;font-weight:bold;margin:0;letter-spacing:0.05em;">QUOTATION</h1><p style="font-size:18px;font-weight:600;color:#4f46e5;margin:4px 0 0;">${quoteNum}</p><p style="font-size:12px;margin:12px 0 0;">Date: ${fmtDate(quoteDate)}</p><p style="font-size:12px;margin:2px 0 0;">Valid Until: ${fmtDate(validUntil)}</p></div></div>
<hr style="border:0;border-top:2px solid #e5e7eb;margin:16px 0;" />
<div style="margin-bottom:16px;"><p style="font-size:11px;font-weight:600;color:#9ca3af;text-transform:uppercase;margin:0 0 4px;">Bill To</p><p style="font-size:16px;font-weight:600;margin:0;">${leadName}</p><p style="font-size:13px;color:#6b7280;margin:0;">${leadCompany}</p></div>
<table style="width:100%;border-collapse:collapse;margin-bottom:16px;"><thead><tr style="background:#f3f4f6;"><th style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:left;">#</th><th style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:left;">Description</th><th style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:center;">Qty</th><th style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:right;">Rate</th><th style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:center;">Tax</th><th style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:right;">Amount</th></tr></thead><tbody>${itemsHtml}</tbody></table>
<div style="display:flex;justify-content:flex-end;margin-bottom:16px;"><div style="width:240px;"><div style="display:flex;justify-content:space-between;font-size:12px;padding:4px 0;"><span>Subtotal</span><span>${fmtCurr(subtotal)}</span></div><div style="display:flex;justify-content:space-between;font-size:12px;padding:4px 0;"><span>Tax</span><span>${fmtCurr(taxTotal)}</span></div><hr style="border:0;border-top:1px solid #e5e7eb;margin:4px 0;" /><div style="display:flex;justify-content:space-between;font-size:15px;font-weight:bold;padding:4px 0;color:#4f46e5;"><span>Total</span><span>${fmtCurr(total)}</span></div></div></div>
${q.terms ? `<div style="border-top:2px solid #e5e7eb;padding-top:12px;margin-bottom:16px;"><p style="font-size:11px;font-weight:600;color:#9ca3af;text-transform:uppercase;margin:0 0 4px;">Terms &amp; Conditions</p><p style="font-size:11px;white-space:pre-line;margin:0;">${q.terms.replace(/\n/g, "<br>")}</p></div>` : ""}
<div style="border-top:2px solid #e5e7eb;padding-top:12px;display:flex;justify-content:space-between;"><div><p style="font-size:12px;font-weight:600;margin:0;">Prepared by</p><p style="font-size:12px;margin:2px 0 0;">${agentName}</p></div><div style="text-align:right;"><p style="font-size:11px;margin:0;">Authorized Signatory</p></div></div>
<p style="text-align:center;font-size:11px;color:#9ca3af;margin-top:24px;">Thank you for your business &mdash; ${companyInfo.name}</p></div>`;
}